import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import { cn } from "../../lib/utils";
import { useSidebar } from "../../contexts/SidebarContext";
import { motion } from "framer-motion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../shadcn/ui/tooltip";
import { getVersionString } from "../../utils/version";

// Style definition for all SVG icons in the sidebar
const sidebarIconStyle = {
  width: "24px",
  height: "24px",
  display: "block",
};

export function Sidebar({
  items = [],
  user,
  onLogout,
  className,
  backgroundColor = "#343434",
  textColor = "white",
  hoverBackgroundColor = "#444444",
  activeColor = "#3B82F6",
  logoProps = {
    src: "/assets/img/spacia/web/spacia_logo_new.png",
    alt: "Spacia Logo",
    className: "h-24 w-auto",
  },
  ...props
}) {
  const location = useLocation();
  const history = useHistory();
  const { collapsed, mobileOpen, toggleSidebar, toggleMobileSidebar, closeMobileSidebar } = useSidebar();
  const [expandedItems, setExpandedItems] = useState({});
  const [sidebarWidth, setSidebarWidth] = useState(collapsed ? 78 : 256); // 256px = 16rem (default width), 77px = 4.8125rem (collapsed width)
  const [isResizing, setIsResizing] = useState(false);
  const [showTooltips, setShowTooltips] = useState(true);
  const resizeHandleRef = useRef(null);
  const sidebarRef = useRef(null);

  const matchesPath = targetPath => {
    if (!targetPath) return false;
    if (targetPath === "/") {
      return location.pathname === "/";
    }
    return location.pathname === targetPath || location.pathname.startsWith(`${targetPath}/`);
  };

  const matchesAnyRoute = routes => Array.isArray(routes) && routes.some(route => matchesPath(route));

  // Initialize expanded state based on active route
  useEffect(() => {
    const newExpandedState = {};

    items.forEach(item => {
      if (item.children) {
        const isActive = item.children.some(
          child => location.pathname === child.href || (child.href && location.pathname.startsWith(child.href + "/"))
        );

        if (isActive) {
          newExpandedState[item.label] = true;
        }
      }
    });

    setExpandedItems(prev => ({ ...prev, ...newExpandedState }));
  }, [location.pathname, items]);

  // Toggle submenu expanded state
  const toggleSubmenu = label => {
    setExpandedItems(prev => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  // Handle resize functionality
  useEffect(() => {
    const handleMouseDown = e => {
      if (window.innerWidth < 768) return; // Don't resize on mobile

      e.preventDefault();
      e.stopPropagation();
      document.body.style.cursor = "ew-resize";
      setIsResizing(true);
    };

    const handleMouseMove = e => {
      if (!isResizing) return;

      const newWidth = e.clientX;
      // Constrain width between 77px and 400px
      if (newWidth >= 78 && newWidth <= 400) {
        setSidebarWidth(newWidth);
        if (sidebarRef.current) {
          sidebarRef.current.style.width = `${newWidth}px`;
        }
      }
    };

    const handleMouseUp = () => {
      if (isResizing) {
        document.body.style.cursor = "default";
        setIsResizing(false);

        // If width is less than 150px, collapse the sidebar
        if (sidebarWidth < 150 && !collapsed) {
          toggleSidebar();
        } else if (sidebarWidth >= 150 && collapsed) {
          toggleSidebar();
        }
      }
    };

    const resizeHandle = resizeHandleRef.current;
    if (resizeHandle) {
      resizeHandle.addEventListener("mousedown", handleMouseDown);
    }

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      if (resizeHandle) {
        resizeHandle.removeEventListener("mousedown", handleMouseDown);
      }
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, sidebarWidth, collapsed, toggleSidebar]);

  // Update width when collapsed state changes
  useEffect(() => {
    setSidebarWidth(collapsed ? 78 : 256);
  }, [collapsed]);

  // Handle tooltip visibility when sidebar collapses
  useEffect(() => {
    if (collapsed && window.innerWidth >= 768) {
      // Immediately disable tooltips
      setShowTooltips(false);

      // Simple approach: just hide all tooltips
      setTimeout(() => {
        const tooltipElements = document.querySelectorAll("[data-radix-tooltip-content]");
        tooltipElements.forEach(element => {
          element.remove();
        });
      }, 50);

      // Re-enable tooltips after animation completes
      setTimeout(() => {
        setShowTooltips(true);
      }, 400);
    }
  }, [collapsed]);

  return (
    <TooltipProvider delayDuration={0}>
      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden"
          aria-hidden="true"
          onClick={() => closeMobileSidebar()}
        />
      )}

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        data-sidebar
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex flex-col transition-all duration-400",
          mobileOpen ? "translate-x-0 shadow-xl" : "-translate-x-full",
          "md:sticky md:top-0 md:translate-x-0 md:shadow-none md:h-screen",
          "w-4/5 max-w-[300px]", // Responsive width on mobile
          className
        )}
        style={{
          width: window.innerWidth >= 768 ? `${sidebarWidth}px` : undefined,
          backgroundColor: backgroundColor,
          color: textColor,
          transition: "width 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
        {...props}
      >
        {/* Header with logo */}
        <div
          className={cn(
            "pt-6 pb-0",
            collapsed
              ? "flex flex-col items-center"
              : "pb-4 flex flex-row items-center md:justify-between justify-center px-4"
          )}
          style={{
            paddingBottom: collapsed ? "12px" : "24px",
            transition: "padding-bottom 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {/* Logo */}
          <motion.div
            className={cn(
              "flex items-center",
              collapsed ? "justify-center" : "md:justify-start justify-center w-full md:w-auto"
            )}
            layout
          >
            <img
              src={logoProps.src}
              alt={logoProps.alt}
              className={cn(collapsed ? "h-16 w-auto" : logoProps.className)}
            />
          </motion.div>

          {/* Toggle button - ONLY visible on desktop (md and above) */}
          <motion.div
            className={cn(
              "!hidden md:!flex", // Hide on mobile, show on desktop
              collapsed ? "justify-center w-full mt-2" : "flex"
            )}
            layout
            style={{
              marginTop: collapsed ? "16px" : "0px",
              transition: "margin-top 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <button
              type="button"
              onClick={toggleSidebar}
              className={`p-1 rounded-md hidden md:block hover:bg-opacity-70`}
              style={{
                color: textColor,
                backgroundColor: "transparent",
                hover: { backgroundColor: hoverBackgroundColor },
              }}
            >
              <span className="sr-only">{collapsed ? "Expand sidebar" : "Collapse sidebar"}</span>
              {collapsed ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M13 17L18 12L13 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6 17L11 12L6 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M18 17L13 12L18 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M11 17L6 12L11 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          </motion.div>
        </div>

        {/* Sidebar content */}
        <div
          className="flex-1 overflow-auto"
          style={{
            marginTop: collapsed ? "16px" : "0px",
            transition: "margin-top 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {/* Navigation */}
          <nav
            className="space-y-1"
            style={{
              paddingTop: collapsed ? "8px" : "0px",
              transition: "padding-top 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            {items.map(item => {
              const matchesSelf = matchesPath(item.href);
              const matchesExtras = matchesAnyRoute(item.activeRoutes);
              const matchesChildren =
                item.children &&
                item.children.some(child => matchesPath(child.href) || matchesAnyRoute(child.activeRoutes));
              const isActive = matchesSelf || matchesExtras || matchesChildren;

              // If the item has children, render submenu
              if (item.children) {
                const isExpanded = expandedItems[item.label];

                return (
                  <div key={item.label} className="space-y-0.5">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => toggleSubmenu(item.label)}
                          className={cn(
                            "w-full group flex items-center justify-between py-2 px-4 text-sm font-normal border-l-4 relative",
                            isActive
                              ? "border-[color:var(--active-color)]"
                              : "border-transparent hover:border-[color:var(--active-color)]/50",
                            collapsed && "md:justify-center md:px-2"
                          )}
                          style={{
                            color: isActive ? activeColor : textColor,
                            "--active-color": activeColor,
                            ...(isActive ? { backgroundColor: `${hoverBackgroundColor}` } : {}),
                            "&:hover": {
                              backgroundColor: hoverBackgroundColor,
                            },
                          }}
                        >
                          <div className={cn("flex items-center gap-3", collapsed && "md:justify-center md:w-full")}>
                            {/* Render the icon directly without cloning */}
                            <span
                              className={cn("flex-shrink-0", collapsed ? "mx-auto" : "mr-2")}
                              style={{ color: isActive ? activeColor : textColor }}
                            >
                              {item.icon ? (
                                <span style={sidebarIconStyle}>{item.icon}</span>
                              ) : (
                                <svg
                                  style={sidebarIconStyle}
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <circle cx="12" cy="12" r="10" />
                                  <line x1="12" y1="8" x2="12" y2="16" />
                                  <line x1="8" y1="12" x2="16" y2="12" />
                                </svg>
                              )}
                            </span>
                            <div
                              className="overflow-hidden flex-1"
                              style={{
                                opacity: !collapsed || window.innerWidth < 768 ? 1 : 0,
                                maxWidth: !collapsed || window.innerWidth < 768 ? "100%" : 0,
                                transition:
                                  "opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), max-width 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                                pointerEvents: !collapsed || window.innerWidth < 768 ? "auto" : "none",
                              }}
                            >
                              <span
                                className="truncate"
                                style={{
                                  display: "block",
                                  paddingLeft: !collapsed || window.innerWidth < 768 ? 6 : 0,
                                  transition: "padding-left 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                                }}
                              >
                                {item.label}
                              </span>
                            </div>
                          </div>

                          {(!collapsed || window.innerWidth < 768) && (
                            <span
                              className={cn("transition-transform duration-200 ml-auto", isExpanded ? "rotate-90" : "")}
                              style={{ color: "rgba(255, 255, 255, 0.6)" }}
                            >
                              ›
                            </span>
                          )}
                        </button>
                      </TooltipTrigger>
                      {collapsed && window.innerWidth >= 768 && showTooltips && (
                        <TooltipContent side="right">
                          <p>{item.label}</p>
                        </TooltipContent>
                      )}
                    </Tooltip>

                    <div
                      className="mt-1 space-y-1 relative ml-8 pl-6"
                      style={{
                        opacity: (!collapsed || window.innerWidth < 768) && isExpanded ? 1 : 0,
                        maxHeight: (!collapsed || window.innerWidth < 768) && isExpanded ? "1000px" : 0,
                        transition:
                          "opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                        overflow: "hidden",
                        pointerEvents: (!collapsed || window.innerWidth < 768) && isExpanded ? "auto" : "none",
                      }}
                    >
                      {/* Vertical line connecting parent to submenu items - positioned under the icon */}
                      <div className="absolute left-1 top-0 bottom-0 w-px bg-gray-500/30"></div>

                      {item.children.map(child => {
                        const isChildActive = matchesPath(child.href) || matchesAnyRoute(child.activeRoutes);

                        return (
                          <Link
                            key={child.href}
                            to={child.href}
                            onClick={() => window.innerWidth < 768 && closeMobileSidebar()}
                            className={cn(
                              "group flex items-center py-2 text-sm font-normal relative pl-6",
                              isChildActive ? "" : "hover:text-white"
                            )}
                            style={{
                              color: isChildActive ? activeColor : textColor,
                              ...(isChildActive
                                ? {
                                    backgroundColor: `${hoverBackgroundColor}`,
                                  }
                                : {}),
                            }}
                          >
                            {/* Active highlight - positioned so it looks like a border but isn't actually adjacent to text */}
                            {isChildActive && (
                              <div
                                className="absolute left-0 top-0 bottom-0 w-1"
                                style={{ backgroundColor: activeColor }}
                              ></div>
                            )}

                            {child.label}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              // Regular menu item without children
              return (
                <Tooltip key={item.href}>
                  <TooltipTrigger asChild>
                    <Link
                      to={item.href}
                      onClick={() => window.innerWidth < 768 && closeMobileSidebar()}
                      className={cn(
                        "group flex items-center py-2 px-4 text-sm font-normal border-l-4",
                        isActive
                          ? "border-[color:var(--active-color)]"
                          : "border-transparent hover:border-[color:var(--active-color)]/50",
                        collapsed && "md:justify-center md:px-2"
                      )}
                      style={{
                        color: isActive ? activeColor : textColor,
                        "--active-color": activeColor,
                        // ...(isActive ? { backgroundColor: `${hoverBackgroundColor}` } : {}),
                        "&:hover": {
                          backgroundColor: hoverBackgroundColor,
                        },
                      }}
                    >
                      {/* Render the icon directly without cloning */}
                      <span
                        className={cn("flex-shrink-0", collapsed ? "mx-auto" : "mr-3")}
                        style={{ color: isActive ? activeColor : textColor }}
                      >
                        {item.icon ? (
                          <span style={sidebarIconStyle}>{item.icon}</span>
                        ) : (
                          <svg
                            style={sidebarIconStyle}
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="16" />
                            <line x1="8" y1="12" x2="16" y2="12" />
                          </svg>
                        )}
                      </span>
                      <div
                        className="overflow-hidden flex-1"
                        style={{
                          opacity: !collapsed || window.innerWidth < 768 ? 1 : 0,
                          maxWidth: !collapsed || window.innerWidth < 768 ? "100%" : 0,
                          transition:
                            "opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), max-width 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                          pointerEvents: !collapsed || window.innerWidth < 768 ? "auto" : "none",
                        }}
                      >
                        <span
                          className="truncate"
                          style={{
                            display: "block",
                            paddingLeft: !collapsed || window.innerWidth < 768 ? 6 : 0,
                            transition: "padding-left 0.3s ease-in-out",
                          }}
                        >
                          {item.label}
                        </span>
                      </div>
                    </Link>
                  </TooltipTrigger>
                  {collapsed && window.innerWidth >= 768 && showTooltips && (
                    <TooltipContent side="right">
                      <p>{item.label}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              );
            })}
          </nav>
        </div>

        {/* Footer with powered by text */}
        <div
          className="p-3 text-center whitespace-nowrap"
          style={{
            color: textColor,
            paddingLeft: collapsed ? 4 : 12,
            paddingRight: collapsed ? 4 : 12,
            transition: "padding 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {/* Full text - shown when expanded or on mobile */}
          <div
            className={cn("md:block", collapsed ? "md:hidden" : "")}
            style={{
              fontSize: "10px",
              transition: "opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            Powered by theSOFTtribe
          </div>

          {/* Stacked text - shown only when collapsed on desktop */}
          <div
            className={cn("hidden", collapsed ? "md:block" : "")}
            style={{
              fontSize: "9px",
              textAlign: "center",
              lineHeight: "1.2",
              transition: "opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <div>Powered by</div>
            <div>theSOFTtribe</div>
          </div>

          {/* Version Number */}
          <div className="text-center pt-2">
            <span className="text-xs text-gray-400">{getVersionString()}</span>
          </div>
        </div>

        {/* Mobile controls (only shown on mobile) */}
        <div className="md:hidden border-t p-4" style={{ borderColor: "rgba(255, 255, 255, 0.1)" }}>
          <button
            type="button"
            onClick={closeMobileSidebar}
            className="w-full text-white rounded py-2"
            style={{ backgroundColor: hoverBackgroundColor }}
          >
            Close Menu
          </button>
        </div>

        {/* Resize handle - only visible on desktop */}
        <div
          ref={resizeHandleRef}
          className="hidden md:block absolute inset-y-0 right-0 w-1 cursor-ew-resize hover:bg-blue-500 transition-colors duration-200"
          aria-hidden="true"
        ></div>
      </div>
    </TooltipProvider>
  );
}
